import React from "react";
import {
  Plan,
  BenefitItemProps,
  AdditionalSupplementalBenefitCoverage,
  PlanDetailsSection as PDS,
  PlanDetailsSection,
} from "../../../@types";
import {
  ItemIterator,
  PlanDetailSection,
  PlanDetailList,
  BenefitItem,
  PlanDetailListWithHelpDrawer,
  DetailItem,
} from "../../../components";
import {
  makeBenefitItemItems as mBII,
  useTranslate,
  planDataUtils,
} from "../../../helpers";
import {
  shouldSuppressPlanBenefits,
  getAdditionalBenefit,
} from "../../../helpers/planHelpers";
import { hasOpioidTreatmentBenefit } from "../../../helpers/benefitHelpers";

export const SpecialBenefitItem = (
  t: (str: string) => string,
  plan: Plan,
  {
    label,
    category,
    benefit,
  }: {
    label: string;
    category: string;
    benefit: string;
  }
): JSX.Element | null => {
  const detail = getAdditionalBenefit(
    plan,
    "special_benefits",
    category,
    benefit
  );

  return detail ? (
    <DetailItem
      key={`SpecialBenefit_${benefit}`}
      label={label}
      detail={t(detail.coverage.toLowerCase())}
      suppressed={shouldSuppressPlanBenefits(plan)}
    />
  ) : null;
};

export const MMPBenefitItem = (
  t: (str: string) => string,
  plan: Plan,
  {
    label,
    category,
    benefit,
  }: {
    label: string;
    category: string;
    benefit: string;
  }
): JSX.Element | null => {
  const detail = getAdditionalBenefit(plan, "mmp_benefits", category, benefit);
  let coverageKey = "";
  if (detail) {
    if (
      detail.coverage ===
      AdditionalSupplementalBenefitCoverage.SB_COVERAGE_SOME_COVERAGE
    ) {
      coverageKey = detail.coverage + "_MMP";
    } else {
      coverageKey = detail.coverage;
    }
  }

  return detail ? (
    <DetailItem
      label={label}
      detail={t(coverageKey.toLowerCase())}
      suppressed={shouldSuppressPlanBenefits(plan)}
    />
  ) : null;
};

export const MoreBenefitItem = (
  t: (str: string) => string,
  plan: Plan,
  {
    label,
    category,
    benefit,
  }: {
    label: string;
    category: string;
    benefit: string;
  },
  defaultToNotCovered = false
): JSX.Element | null => {
  const detail = getAdditionalBenefit(
    plan,
    "other_benefits",
    category,
    benefit
  );

  return detail ? (
    <DetailItem
      key={`OtherBenefit_${benefit}`}
      label={label}
      detail={t(detail.coverage.toLowerCase())}
      suppressed={shouldSuppressPlanBenefits(plan)}
    />
  ) : defaultToNotCovered ? (
    <DetailItem
      key={`OtherBenefit_${benefit}`}
      label={label}
      detail={t("sb_coverage_not_covered")}
      suppressed={shouldSuppressPlanBenefits(plan)}
    />
  ) : null;
};

const ExtraBenefits: React.FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const makeBenefitItemItems = (
    items: Array<{ label: string | JSX.Element; service?: string }>,
    benefit: string
  ): BenefitItemProps[] => mBII(items, benefit, plan);

  const t = useTranslate();

  const hasMoreBenefits =
    plan.additional_supplemental_benefits &&
    !!plan.additional_supplemental_benefits.other_benefits.length;
  const onlyMoreBenefitIsOpioidTreatment =
    plan.additional_supplemental_benefits &&
    plan.additional_supplemental_benefits.other_benefits.length === 1 &&
    hasOpioidTreatmentBenefit(plan);

  const specialBenefits = [
    {
      label: t("special_benefit.food_and_produce"),
      category: "non_primarily_health_related_benefits",
      benefit: "food_and_produce",
    },
    {
      label: t("special_benefit.meals"),
      category: "non_primarily_health_related_benefits",
      benefit: "meals_beyond_limited_basis",
    },
    {
      label: t("special_benefit.pest_control"),
      category: "non_primarily_health_related_benefits",
      benefit: "pest_control",
    },
    {
      label: t("special_benefit.transportation_non_medical"),
      category: "non_primarily_health_related_benefits",
      benefit: "transportation_for_non_medical_needs",
    },
    {
      label: t("special_benefit.air_quality"),
      category: "non_primarily_health_related_benefits",
      benefit: "indoor_air_quality_equipment_and_services",
    },
    {
      label: t("special_benefit.social_needs"),
      category: "non_primarily_health_related_benefits",
      benefit: "social_needs_benefit",
    },
    {
      label: t("special_benefit.complementary_therapies"),
      category: "non_primarily_health_related_benefits",
      benefit: "complementary_therapies",
    },
    {
      label: t("special_benefit.self_direction"),
      category: "non_primarily_health_related_benefits",
      benefit: "services_supporting_self_direction",
    },
    {
      label: t("special_benefit.home_modifications"),
      category: "non_primarily_health_related_benefits",
      benefit: "structural_home_modifications",
    },
    {
      label: t("special_benefit.general_supports"),
      category: "non_primarily_health_related_benefits",
      benefit: "transitional_temporary_supports",
    },
    {
      label: t("special_benefit.reduced_cost_sharing"),
      category: "reduced_cost_sharing_for_vbid_uf_ssbci",
      benefit: "reduced_cost_sharing_for_vbid_uf_ssbci",
    },
  ];

  const moreBenefits = [
    {
      label: t("more_benefits.fitness"),
      category: "defined_supplemental_benefits",
      benefit: "fitness_benefit",
    },
    {
      label: t("more_benefits.transportation_non_emergency_any_health"),
      category: "transportation_services",
      benefit: "any_health_related_location",
    },
    {
      label: t("more_benefits.transportation_non_emergency_plan_approved"),
      category: "transportation_services",
      benefit: "plan_approved_health_related_location",
    },
    {
      label: t("more_benefits.over_the_counter_drugs"),
      category: "otc_items",
      benefit: "otc_items",
    },
    {
      label: t("more_benefits.in_home_support"),
      category: "defined_supplemental_benefits",
      benefit: "in_home_support_services",
    },
    {
      label: t("more_benefits.worldwide_emergency"),
      category: "worldwide_emergency_transportation",
      benefit: "worldwide_emergency_coverage",
    },
    {
      label: t("more_benefits.worldwide_urgent"),
      category: "worldwide_emergency_transportation",
      benefit: "worldwide_urgent_care",
    },
    {
      label: t("more_benefits.response_device"),
      category: "defined_supplemental_benefits",
      benefit: "personal_emergency_response_sys_pers",
    },
    {
      label: t("more_benefits.chiropractic_service"),
      category: "chiropractic_services",
      benefit: "routine_care",
    },
    {
      label: t("more_benefits.bathroom_safety_devices"),
      category: "defined_supplemental_benefits",
      benefit: "bathroom_safety_devices",
    },
    {
      label: t("more_benefits.meals"),
      category: "meal_benefit",
      benefit: "meal_benefit",
    },

    {
      label: t("more_benefits.annual_physical"),
      category: "annual_physical_exam",
      benefit: "annual_physical_exam",
    },
    {
      label: t("more_benefits.telehealth"),
      category: "additional_telehealth_services",
      benefit: "medicare_covered_visits",
    },
    {
      label: t("more_benefits.worldwide_emergency"),
      category: "worldwide_emergency_urgent_coverage",
      benefit: "worldwide_emergency_coverage",
      defaultToNotCovered: true,
    },
  ];

  const additionalBenefitsForMMP = [
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.non_medicare_covered_prosthetics_medical_supplies"
      ),
      category: "prosthetics_medical_supplies",
      benefit: "non_medicare_covered_prosthetics_medical_supplies",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.early_and_periodic_screening"
      ),
      category: "additional_services",
      benefit: "early_and_periodic_screening",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.tobacco_cessation_counseling_for_pregnant_women"
      ),
      category: "additional_services",
      benefit: "tobacco_cessation_counseling_for_pregnant_women",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.freestanding_birth_center_services"
      ),
      category: "additional_services",
      benefit: "freestanding_birth_center_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.respiratory_care_services"
      ),
      category: "additional_services",
      benefit: "respiratory_care_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.family_planning_services"
      ),
      category: "additional_services",
      benefit: "family_planning_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.nursing_home_services"
      ),
      category: "additional_services",
      benefit: "nursing_home_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.home_and_community_based_services"
      ),
      category: "additional_services",
      benefit: "home_and_community_based_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.personal_care_services"
      ),
      category: "additional_services",
      benefit: "personal_care_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.self_directed_personal_assistance_services"
      ),
      category: "additional_services",
      benefit: "self_directed_personal_assistance_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.private_duty_nursing_services"
      ),
      category: "additional_services",
      benefit: "private_duty_nursing_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.case_management_long_term_care"
      ),
      category: "additional_services",
      benefit: "case_management_long_term_care",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.institution_for_mental_disease_services_for_65_or_older"
      ),
      category: "additional_services",
      benefit: "institution_for_mental_disease_services_for_65_or_older",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.services_in_an_intermediate_care_facility_for_disabilities"
      ),
      category: "additional_services",
      benefit: "services_in_an_intermediate_care_facility_for_disabilities",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.case_management"
      ),
      category: "additional_services",
      benefit: "case_management",
    },
  ];

  let hearingBenefits = [
    {
      label: t("plan_terms.hearing.exam"),
      service: "hearing_exam",
    },
    {
      label: t("plan_terms.hearing.fitting_evaluation"),
      service: "fitting_evaluation",
    },
  ];

  if (
    plan.package_benefits.BENEFIT_HEARING.benefit_services.SERVICE_HEARING_AIDS
  ) {
    hearingBenefits.push({
      label: t("plan_terms.hearing.aids"),
      service: "hearing_aids",
    });
  } else {
    hearingBenefits = [
      ...hearingBenefits,
      {
        label: t("plan_terms.hearing.inner_ear"),
        service: "hearing_aids_inner_ear",
      },
      {
        label: t("plan_terms.hearing.outer_ear"),
        service: "hearing_aids_outer_ear",
      },
      {
        label: t("plan_terms.hearing.over_the_ear"),
        service: "hearing_aids_over_the_ear",
      },
    ];
  }

  const nonOpioidBenefits = [
    {
      label: t("plan_terms.chiropractic_services"),
      category: "chiropractic_services",
      benefit: "routine_care",
    },
    {
      label: t("plan_terms.acupuncture"),
      category: "non_opioid_pain_management",
      benefit: "acupuncture",
    },
    {
      label: t("plan_terms.massage_therapy"),
      category: "defined_supplemental_benefits",
      benefit: "therapeutic_massage",
    },
    {
      label: t("plan_terms.alternative_therapies"),
      category: "defined_supplemental_benefits",
      benefit: "alternative_therapies",
    },
  ];

  return (
    <>
      <PlanDetailSection
        id={PDS.EXTRA_BENEFITS}
        title={t("plan_terms.extra_benefits_and_costs")}
      >
        <PlanDetailList id={PDS.HEARING} title={t("plan_terms.hearing")}>
          <ItemIterator Component={BenefitItem}>
            {makeBenefitItemItems(hearingBenefits, "hearing")}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailListWithHelpDrawer
          id={PlanDetailsSection.PREVENTIVE_DENTAL}
          titleId="plan_terms.preventive_dental.short"
          helpId="plan_terms.preventive_dental.definition"
          tealiumProps={{
            event_action: "Plan Details",
            event_label: "Toggle - preventive dental",
          }}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.preventive_dental.oral_exam"),
                    service: "oral_exam",
                  },
                  {
                    label: t("plan_terms.preventive_dental.cleaning"),
                    service: "cleaning",
                  },
                  {
                    label: t("plan_terms.preventive_dental.flouride_treatment"),
                    service: "fluoride_treatment",
                  },
                  {
                    label: t("plan_terms.preventive_dental.dental_xrays"),
                    service: "dental_xrays",
                  },
                ],
                "preventive_dental"
              ),
            ]}
          </ItemIterator>
        </PlanDetailListWithHelpDrawer>

        <PlanDetailListWithHelpDrawer
          id={PlanDetailsSection.COMPREHENSIVE_DENTAL}
          titleId="plan_terms.comprehensive_dental.short"
          helpId="plan_terms.comprehensive_dental.definition"
          tealiumProps={{
            event_action: "Plan Details",
            event_label: "Toggle - comprehensive dental",
          }}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t(
                      "plan_terms.comprehensive_dental.nonroutine_services"
                    ),
                    service: "nonroutine_services",
                  },
                  {
                    label: t(
                      "plan_terms.comprehensive_dental.diagnostic_services"
                    ),
                    service: "diagnostic_services",
                  },
                  {
                    label: t(
                      "plan_terms.comprehensive_dental.restorative_services"
                    ),
                    service: "restorative_services",
                  },
                  {
                    label: t("plan_terms.comprehensive_dental.endodontics"),
                    service: "endodontics",
                  },
                  {
                    label: t("plan_terms.comprehensive_dental.periodontics"),
                    service: "periodontics",
                  },
                  {
                    label: t("plan_terms.comprehensive_dental.extractions"),
                    service: "extractions",
                  },
                  {
                    label: t(
                      "plan_terms.comprehensive_dental.prosthodontics_and_other_services"
                    ),
                    service: "prosthodontics_and_other_services",
                  },
                ],
                "comprehensive_dental"
              ),
            ]}
          </ItemIterator>
        </PlanDetailListWithHelpDrawer>

        <PlanDetailList id={PDS.VISION} title={t("plan_terms.vision")}>
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.vision.routine_eye_exam"),
                    service: "routine_eye_exam",
                  },
                  {
                    label: t("plan_terms.vision.contact_lenses"),
                    service: "contact_lenses",
                  },
                  {
                    label: t("plan_terms.vision.eyeglasses"),
                    service: "eyeglasses_frames_and_lenses",
                  },
                  {
                    label: t("plan_terms.vision.eyeglass_frames_only"),
                    service: "eyeglass_frames",
                  },
                  {
                    label: t("plan_terms.vision.eyeglass_lenses_only"),
                    service: "eyeglass_lenses",
                  },
                  {
                    label: t("plan_terms.vision.upgrades"),
                    service: "upgrades",
                  },
                ],
                "vision"
              ),
            ]}
          </ItemIterator>
        </PlanDetailList>

        <PlanDetailList
          id={PDS.NON_OPIOID_PAIN_MANAGEMENT}
          title={t("plan_terms.non_opioid_pain_management")}
        >
          {nonOpioidBenefits.map(benefit =>
            MoreBenefitItem(t, plan, benefit, true)
          )}
        </PlanDetailList>

        {hasMoreBenefits && !onlyMoreBenefitIsOpioidTreatment && (
          <PlanDetailList
            id={PDS.MORE_BENEFITS}
            title={t("more_benefits.title")}
          >
            {moreBenefits.map(benefit =>
              MoreBenefitItem(t, plan, benefit, benefit.defaultToNotCovered)
            )}
          </PlanDetailList>
        )}

        {plan.additional_supplemental_benefits &&
          plan.additional_supplemental_benefits.special_benefits.length > 0 && (
            <PlanDetailList
              id={PDS.SPECIAL_BENEFITS}
              title={t("special_benefit.special_benefits")}
              titleChildren={
                <p className="ds-u-font-size--base ds-u-margin-bottom--0 ds-u-sans ds-u-font-weight--normal ds-u-text-transform--none">
                  {t("special_benefit.description")}
                </p>
              }
            >
              {specialBenefits.map(benefit =>
                SpecialBenefitItem(t, plan, benefit)
              )}
            </PlanDetailList>
          )}

        {planDataUtils.showMMPBenefits(plan) && (
          <PlanDetailList
            id={PDS.MMP_ADDITIONAL_BENEFITS}
            title={t("plan_terms.mmp_additional_benefits_and_services")}
          >
            {additionalBenefitsForMMP.map(benefit =>
              MMPBenefitItem(t, plan, benefit)
            )}
          </PlanDetailList>
        )}
      </PlanDetailSection>
    </>
  );
};

export default ExtraBenefits;
